import { ConnectionAnalyserHost } from "./connectionAnalyser/ConnectionAnalyserView";
import React, { useEffect } from "react";
import "./App.scss";
import { noItx, itx } from "./translationItx/itx";

import SignalType from "./models/SignalType";
import SignalTypes from "./models/SignalTypes";

import Menubar from "./components/Menubar";
import MainAppHeader from "./components/MainAppHeader";
import AppWorkspace from "./components/AppWorkspace";
import { askToSaveProjectBeforeLeaving } from "./components/dialogs/SaveProjectBeforeLeaving";

import { useNavigate } from "react-router-dom";
import { Suspense } from "react";
import "@fontsource/montserrat";
import { isUiModeDeveloper, isUiModeExpert, isUiModeProductAdmin } from "./uiMode";

import { usePreventZoom } from "./components/UsePreventZoom";
import Browser from "./browser";
import { useRedraw } from "./models/useRerender";
import FullSceenButton from "./components/FullscreenButton";
import GlobalWaitIndicator, { showGlobalWaitIndicator } from "./components/GlobalWaitIndicator";
import { showOfflineStatus } from "./components/OfflineStatus";
import { ApplicationMode } from "./models/SettingsEnums";
import { useOnChangeWatcher } from "./models/useOnChangeWatcher";

declare const __APP_VERSION__: string;

var myHistory: any = [];

const App: React.FC = () => {
  useOnChangeWatcher(state.settings, property => property === "language");
  state.settings.applicationMode = ApplicationMode.Main;
  state.projectMetadata.applicationMode = ApplicationMode.Main;
  const navigate = useNavigate();

  const [isLoaded, setIsLoaded] = React.useState(false);

  useEffect(() => {
    if (!isLoaded || !state.projectMetadata.isC1Project) return;
    const project = state.c1Project;
    let disposed = false;
    // Load after model initialization: the generator's furniture registry uses Button/Device.
    void import("../ConceptOne/src/estada/C1_generateTags").then(({ generateTags }) => {
      if (!disposed && state.c1Project === project) generateTags(project);
    });
    return () => { disposed = true; };
  }, [isLoaded, state.projectMetadata.id]);

  const redraw = useRedraw();

  usePreventZoom();

  if (Browser.isSafariIos()) {
    history.pushState(null, "", location.href);
    window.onpopstate = function (event) {
      history.go(1);
    };
  }

  function onUnload() {
    try {
      state.eventLogger.endSession();
    } catch (ex) {}
    try {
      if (state.documentScreen2) {
        state.documentScreen2.defaultView?.close();
      }
    } catch (ex) {}
  }

  function alertUser(ev: any) {
    if (state.isDirty) {
      ev.preventDefault();
      askToSaveProjectBeforeLeaving();
      ev.returnValue = "";
    }
  }

  function popstate(ev: any) {
    if (Browser.isMobile()) {
      //
      // Disable "Navigate Back" to avoid erroneously swipes from the left screen border
      //
      history.pushState(null, document.title, location.href);
    }
  }

  function autoScrollUp() {
    if (!Browser.isInFullscreenMode()) {
      window.scrollBy(0, 200);
    }
  }

  useEffect(() => {
    ///// while browser reload go back to projectselection
    let disposed = false;
    let cleanup: (() => void) | undefined;

    void init().then((dispose) => {
      if (disposed) {
        dispose();
      } else {
        cleanup = dispose;
      }
    });

    return () => {
      disposed = true;
      cleanup?.();
    };
  }, []);

  useEffect(() => {
    if (!isLoaded) return;
    // The C1 and main routes share the indicator module but mount separate DOM
    // hosts. A pending C1 nesting count must not keep the fresh main host open.
    void showGlobalWaitIndicator(false);
  }, [isLoaded]);

  async function init(): Promise<() => void> {
    if (!(window as any).isEstadaLoggedIn) {
      const targetUrl = window.location.origin;
      window.location.assign(targetUrl);
    }

    document.title = `ESTADA: ${state.projectMetadata.name}`;
    state.documentScreen1 = document;

    window.addEventListener("beforeunload", alertUser);
    window.addEventListener("unload", onUnload);

    // not working too: window.addEventListener("popstate", alertUser);

    // MWE 17.8.2023
    // disable navigate back with the browser buttons
    // because we will loose all our data !!!!!!!!
    //

    /// no valid: (location as any).replace();

    state._navigate = navigate;

    state.clipboard.clear();

    const rootElm = document.getElementById("root");
    let orientationRedrawTimer: ReturnType<typeof setTimeout> | undefined;
    let guideProjectTimer: ReturnType<typeof setTimeout> | undefined;
    let onlineStatusTimer: ReturnType<typeof setInterval> | undefined;

    // setTimeout(() => {
    //   const rootElm = document.getElementById("root");
    //   if (rootElm) {
    //     document.body.style.height = `${window.innerHeight + 200}px`;
    //     //rootElm.style.height = "100vh";
    //     document.body.style.overflow = `auto`;
    //     document.body.style.touchAction = `none`;
    // }

    function onOrientationChange() {
      if (orientationRedrawTimer) clearTimeout(orientationRedrawTimer);
      orientationRedrawTimer = setTimeout(() => {
        orientationRedrawTimer = undefined;
        redraw();
      }, 100);
    }
    window.addEventListener("orientationchange", onOrientationChange);

    window.history.pushState(myHistory, "Estada", window.location.href);
    window.addEventListener("popstate", popstate);

    function onRootTouchStart(_event: Event) {}

    if (rootElm) {
      document.body.style.touchAction = `none`;
      rootElm.style.touchAction = `none`;
      rootElm.addEventListener("touchstart", onRootTouchStart);
    }

    guideProjectTimer = setTimeout(() => {
      guideProjectTimer = undefined;
      state.guideMode.onProjectOpened();
      if (navigator.onLine) {
        showOfflineStatus(false);
      }
    }, 1000);

    onlineStatusTimer = setInterval(() => {
      showOfflineStatus(!navigator.onLine);
    }, 5000);

    // if (Browser.isMobile()) {
    //   setInterval(autoScrollUp, 400, 500);
    // }
    const localStorageReleaseNotes = localStorage.getItem("releaseNotes");

    if (localStorageReleaseNotes != __APP_VERSION__) {
      if (localStorageReleaseNotes) {
        state.activePanes.loadPage(noItx("releaseNotes"));
      }
      localStorage.setItem("releaseNotes", __APP_VERSION__);
    }

    setIsLoaded(true);

    return () => {
      if (orientationRedrawTimer) clearTimeout(orientationRedrawTimer);
      if (guideProjectTimer) clearTimeout(guideProjectTimer);
      if (onlineStatusTimer) clearInterval(onlineStatusTimer);

      window.removeEventListener("orientationchange", onOrientationChange);
      window.removeEventListener("beforeunload", alertUser);
      window.removeEventListener("unload", onUnload);
      window.removeEventListener("popstate", popstate);
      rootElm?.removeEventListener("touchstart", onRootTouchStart);
    };
  }

  useEffect(() => {
    const chat = document.getElementById("lo-messenger-frame");
    const chatTimer = setTimeout(() => {
      if (chat) {
        if (
          state.activePaneId == "home" // no itx
        ) {
          chat.style.display = "block";
        } else {
          chat.style.display = "none";
        }
      }
    }, 100);

    return () => clearTimeout(chatTimer);
  });

  function handleContextMenu(event: any) {
    event.preventDefault();
  }

  var style: any = { userSelect: "none" };
  if (isUiModeDeveloper()) {
    style = {};
  }

  //
  // MWE 2.8.2024
  //
  // Zum Stabilisieren für nicht mobile Anwendung
  // da es vereinzelt zu "verrutschen" kam, bei denen die App sich nach oben berschoben hat
  //

  if (!Browser.isMobile()) {
    ((style.position = "absolute"), (style.left = "0px"), (style.top = "0px"));
  }

  console.log("render App");

  let className = `App ${Browser.isMobile() ? "mobile" : ""} ${Browser.isLandScape() ? "landscape" : "portrait"}`;

  if (Browser.isTouch()) {
    className += " touch";
  } else {
    className += " no-touch";
  }

  if (Browser.isSafariIos()) {
    className += " ios";
  }

  if (Browser.getBrowser().indexOf("Firefox") >= 0) {
    className += " firefox"; // no itx
    state.browserName = "firefox"; // no itx
  }

  if (Browser.usePseudoFullScreenScrollMode()) {
    className += " fullscreenScroll";
  }

  if (isUiModeProductAdmin()) {
    className += " productAdminMode";
  }

  if (isUiModeExpert()) {
    className += " expertMode";
  }

  if (!isLoaded) {
    return <div>{itx("App.loading", "loading ....")}</div>;
  }

  return (
    <div id="App" className={className} onContextMenu={handleContextMenu} style={{ ...style }} onClick={event => {
      if (event.target !== event.currentTarget) return;
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
      state.selectionManager.selectedObject = undefined;
    }}>
      <style>{`
        :root {
          --backgroundColorToolbar: #var(--lightLightGrey)
        }
      `}</style>

      <MainAppHeader />
      <Menubar />
      <ConnectionAnalyserHost />

      <AppWorkspace />

      <div
        id="touchMoveContainer"
        style={{
          position: "absolute",
          zIndex: "99",
          backgroundColor: "transparent",
          pointerEvents: "none",
          width: "100%",
          height: "100%",
        }}
      >
        <div
          id="touchMoveItem"
          style={{
            position: "absolute",
            width: "1x",
            height: "1px",
            left: "0px",
            top: "0px",
          }}
        >
          <div
            id="touchMoveItemContent"
            style={{
              position: "absolute",
              width: "100px",
              height: "100px",
              left: "-50px",
              top: "-50px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              // backgroundColor: "rgba(255,0,0,0.2)",
            }}
          ></div>
        </div>
      </div>

      <FullSceenButton scope="main" />

      <GlobalWaitIndicator />
    </div>
  );
};

// export App;
export default function WrappedApp() {
  return (
    <Suspense fallback={null}>
      <App />
    </Suspense>
  );
}

// test from MWE 1
